import geoEntity from '../../config/geo-entity.json';

const BASE_URL = 'https://www.rintintin-pro.com';

/** Trailing slash sauf `/` — aligné Netlify Pretty URLs (URL 200). Le `?` reste après le path. */
export function normalizeCanonicalPath(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  const queryAt = pathname.indexOf('?');
  const hashAt = pathname.indexOf('#');
  const cuts = [queryAt, hashAt].filter((index) => index >= 0);
  const cut = cuts.length ? Math.min(...cuts) : pathname.length;
  const pathPart = pathname.slice(0, cut);
  const suffix = pathname.slice(cut);
  const path = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
  const withSlash = path === '/' || path.endsWith('/') ? path : `${path}/`;
  return `${withSlash}${suffix}`;
}

export function absoluteCanonicalUrl(pathname: string): string {
  return `${BASE_URL}${normalizeCanonicalPath(pathname)}`;
}

/** Coupe une meta description sur un espace, pas au milieu d'un mot. */
export function clipMetaDescription(value: string, max = 300): string {
  const text = value.replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return (space > 80 ? cut.slice(0, space) : cut).trim();
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface BreadcrumbItem {
  name: string;
  path: string;
}

const WEB_PAGE_IN_LANGUAGE: Record<string, string> = {
  fr: 'fr-FR',
  en: 'en',
  es: 'es',
  de: 'de',
  it: 'it',
  nl: 'nl',
};

export function buildWebPageSchema(page: {
  name: string;
  description: string;
  path: string;
  primaryKeyword?: string;
  /** Chemin /public ou URL absolue — image représentative de la page. */
  imageUrl?: string;
  /** Code langue court (fr, en, nl, …). Défaut fr. */
  locale?: string;
}) {
  const url = `${BASE_URL}${normalizeCanonicalPath(page.path)}`;
  const locale = (page.locale || 'fr').slice(0, 2).toLowerCase();
  const pageImage =
    page.imageUrl &&
    (page.imageUrl.startsWith('http://') || page.imageUrl.startsWith('https://')
      ? page.imageUrl
      : `${BASE_URL}${page.imageUrl.startsWith('/') ? page.imageUrl : `/${page.imageUrl}`}`);

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: page.name,
    description: page.description,
    url,
    inLanguage: WEB_PAGE_IN_LANGUAGE[locale] || 'fr-FR',
    ...(pageImage
      ? {
          image: pageImage,
          primaryImageOfPage: {
            '@type': 'ImageObject',
            url: pageImage,
          },
        }
      : {}),
    isPartOf: {
      '@type': 'WebSite',
      name: 'Rintintin Pro',
      url: BASE_URL,
    },
    about: page.primaryKeyword
      ? {
          '@type': 'Thing',
          name: page.primaryKeyword,
        }
      : undefined,
    publisher: {
      '@type': 'Organization',
      name: 'Rintintin Pro',
      logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo192.png` },
    },
  };
}

function schemaAreaServed(area: string | string[]) {
  const list = (Array.isArray(area) ? area : [area]).filter(Boolean);
  const toPlace = (name: string) =>
    /union européenne|european union|^ue$|^eu$/i.test(name)
      ? { '@type': 'AdministrativeArea' as const, name }
      : { '@type': 'Country' as const, name };
  if (list.length <= 1) {
    return toPlace(list[0] || 'France');
  }
  return list.map(toPlace);
}

/** Schema WebSite. Pas de SearchAction : /blog?q= n'est pas une recherche. */
export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: geoEntity.brand.name,
    url: geoEntity.brand.url,
  };
}

export function buildOrganizationSchema() {
  const addr = geoEntity.legal.address;
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: geoEntity.brand.name,
    legalName: geoEntity.legal.name,
    url: geoEntity.brand.url,
    logo: `${geoEntity.brand.url}${geoEntity.brand.logo}`,
    email: geoEntity.legal.email,
    foundingDate: geoEntity.legal.foundingDate,
    sameAs: geoEntity.sameAs,
    description: geoEntity.brand.tagline,
    areaServed: schemaAreaServed(geoEntity.product.areaServed),
    address: {
      '@type': 'PostalAddress',
      streetAddress: addr.streetAddress,
      postalCode: addr.postalCode,
      addressLocality: addr.addressLocality,
      addressCountry: addr.addressCountry,
    },
  };
}

export function buildSoftwareApplicationSchema(overrides?: {
  name?: string;
  description?: string;
  featureList?: string[];
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: overrides?.name ?? 'Rintintin Pro',
    applicationCategory: 'BusinessApplication',
    operatingSystem: ['Web', 'iOS', 'Android'],
    url: BASE_URL,
    description:
      overrides?.description ??
      'Solution de gestion tout-en-un pour les professionnels canins : éducateurs, dog sitters, pensions canines. Gestion de réservations, paiements en ligne et visibilité accrue.',
    author: { '@type': 'Organization', name: 'Rintintin' },
    offers: [
      {
        '@type': 'Offer',
        name: 'Essentiel',
        price: '0',
        priceCurrency: 'EUR',
        description:
          'Gratuit — planning, fiches clients, page de réservation publique, app mobile iOS & Android. Aucune carte bancaire.',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Premium (annuel)',
        price: '15',
        priceCurrency: 'EUR',
        description:
          '15 €/mois facturé annuellement (essai 7 jours) — paiements Stripe, messagerie, forfaits, facturation, stats. Mensuel : 20 €/mois. L’offre 29 € Prêt-à-Servir est une config ponctuelle, pas l’abonnement.',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Premium (mensuel)',
        price: '20',
        priceCurrency: 'EUR',
        description: '20 €/mois — mêmes fonctionnalités Premium, facturation mensuelle, essai 7 jours.',
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: 'Compte Prêt-à-Servir',
        price: '29',
        priceCurrency: 'EUR',
        description:
          'Prestation de configuration ponctuelle du compte (29 € paiement unique). Ce n’est pas le prix de l’abonnement Premium.',
        availability: 'https://schema.org/InStock',
      },
    ],
    ...(overrides?.featureList
      ? { featureList: overrides.featureList }
      : {}),
  };
}

function faqPlainText(value: string): string {
  return value
    .replace(/\[[^\]]+\]\([^)]+\)/g, (match) => match.replace(/\[([^\]]+)\]\([^)]+\)/, '$1'))
    .replace(/[*_~`>#]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function buildFAQPageSchema(items: FAQItem[]) {
  const filled = items.filter((item) => item.question?.trim() && item.answer?.trim());
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: filled.map((item) => ({
      '@type': 'Question',
      name: faqPlainText(item.question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqPlainText(item.answer),
      },
    })),
  };
}

export function buildHowToSchema(opts: {
  name: string;
  description: string;
  steps: string[];
  locale?: string;
}) {
  const steps = opts.steps.map((s) => s.trim()).filter(Boolean);
  const locale = (opts.locale || 'fr').slice(0, 2).toLowerCase();
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: opts.name,
    description: opts.description,
    inLanguage: WEB_PAGE_IN_LANGUAGE[locale] || 'fr-FR',
    step: steps.map((text, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: text.length > 80 ? `${text.slice(0, 77)}…` : text,
      text,
    })),
  };
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${normalizeCanonicalPath(item.path)}`,
    })),
  };
}

function articleWordCount(content?: string): number {
  if (!content) return 0;
  const plain = content
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]+\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[#*_`>]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return plain ? plain.split(' ').length : 0;
}

export function buildArticleSchema(article: {
  title: string;
  description: string;
  image?: string;
  datePublished: string;
  dateModified?: string;
  authorName: string;
  url: string;
  inLanguage?: string;
  articleSection?: string;
  isPartOfUrl?: string;
  isPartOfName?: string;
  readingTimeMinutes?: number;
  content?: string;
}) {
  const minutes = Number(article.readingTimeMinutes);
  const words = articleWordCount(article.content);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image || `${BASE_URL}/og-image.jpg`,
    inLanguage: article.inLanguage,
    articleSection: article.articleSection || undefined,
    datePublished: article.datePublished,
    dateModified: article.dateModified ?? article.datePublished,
    author:
      article.authorName === 'Rintintin Pro' || article.authorName === 'Rintintin'
        ? { '@type': 'Organization', name: article.authorName, url: BASE_URL }
        : { '@type': 'Person', name: article.authorName },
    publisher: {
      '@type': 'Organization',
      name: 'Rintintin Pro',
      logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo192.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': article.url },
    ...(Number.isFinite(minutes) && minutes > 0
      ? { timeRequired: `PT${Math.round(minutes)}M` }
      : {}),
    ...(words > 0 ? { wordCount: words } : {}),
    ...(article.isPartOfUrl
      ? {
          isPartOf: {
            '@type': 'Blog',
            name: article.isPartOfName || 'Blog Rintintin Pro',
            url: article.isPartOfUrl,
          },
        }
      : {}),
  };
}

/** Liste des articles sur une page blog (découvrabilité pour les moteurs). */
export function buildBlogItemListSchema(
  items: { title: string; slug: string; datePublished?: string }[],
  listPageUrl: string,
  articleBasePath = '/blog',
  listName = 'Articles Rintintin Pro',
  inLanguage = 'fr',
  startPosition = 1,
  totalItems?: number
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: totalItems ?? items.length,
    url: listPageUrl,
    inLanguage,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: startPosition + index,
      item: {
        '@type': 'BlogPosting',
        headline: item.title,
        url: `${BASE_URL}${normalizeCanonicalPath(`${articleBasePath}/${item.slug}`)}`,
        ...(item.datePublished ? { datePublished: item.datePublished } : {}),
      },
    })),
  };
}

export function buildAggregateRatingSchema(opts: {
  itemName: string;
  ratingValue: number;
  bestRating?: number;
  ratingCount: number;
  reviewUrl?: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: opts.itemName,
    url: BASE_URL,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: opts.ratingValue,
      bestRating: opts.bestRating ?? 5,
      ratingCount: opts.ratingCount,
      ...(opts.reviewUrl ? { url: opts.reviewUrl } : {}),
    },
  };
}
